import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, AlertCircle, Check, ArrowRight, Mail, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BackButton from './BackButton';
import './Auth.css';

const API_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/auth`;

export default function Register() {
  const [step, setStep] = useState(1); // 1: form, 2: OTP verification
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const { setAuthData } = useAuth();
  const navigate = useNavigate();

  // Password requirements
  const requirements = [
    { met: password.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { met: /[a-z]/.test(password), text: 'One lowercase letter' },
    { met: /[0-9]/.test(password), text: 'One number' },
  ];

  const allRequirementsMet = requirements.every(r => r.met);

  // Start cooldown timer for resend
  const startResendCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/register/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send verification code');
      }

      setStep(2);
      startResendCooldown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/register/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to verify code');
      }

      // Set auth data and navigate
      setAuthData(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/register/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to resend code');
      }

      startResendCooldown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Left Side - Branding */}
      <div className="login-branding">
        <div className="login-branding-content">
          <BackButton />
          <Link to="/" className="login-logo">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="50" height="50" className="logo-img">
              <rect width="32" height="32" fill="var(--bg-tertiary)"></rect>
              <rect x="6" y="4" width="16" height="5" fill="var(--accent-primary)"></rect>
              <rect x="6" y="4" width="5" height="8" fill="var(--accent-primary)"></rect>
              <rect x="6" y="12" width="16" height="5" fill="var(--accent-primary)"></rect>
              <rect x="17" y="17" width="5" height="7" fill="var(--accent-primary)"></rect>
              <rect x="6" y="23" width="16" height="5" fill="var(--accent-primary)"></rect>
              <polygon points="22,4 26,7 26,12 22,9" fill="#1a1a2e"></polygon>
              <polygon points="22,12 26,15 26,20 22,17" fill="#1a1a2e"></polygon>
              <polygon points="22,23 26,26 26,31 22,28" fill="#1a1a2e"></polygon>
              <polygon points="6,28 22,28 26,31 10,31" fill="#1a1a2e"></polygon>
            </svg>
            <span className="logo-text">SCRAWL</span>
          </Link>
          
          <h1 className="login-headline">
            Join the<br />
            <span className="highlight">community</span>
          </h1>
          
          <p className="login-description">
            Create your free account and start sharing code instantly.
          </p>

          <div className="login-features">
            <div className="login-feature-item">
              <span className="login-feature-text">Access your paste history anytime</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Create private & password-protected pastes</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Syntax highlighting for 100+ languages</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Embed images in your pastes</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">Zero ads, zero tracking, forever free</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="login-form-side">
        <div className="login-form-container register-form-container">
          {step === 1 ? (
            <>
              <div className="login-form-header">
                <h2>Create Account</h2>
                <p>Sign up to get started</p>
              </div>

              <form onSubmit={handleSendOTP} className="login-form">
                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="login-field">
                  <label>Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    placeholder="Choose a username"
                    required
                    autoFocus
                  />
                  <span className="field-hint">Letters, numbers, and underscores only</span>
                </div>

                <div className="login-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div className="login-field">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create a password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {password && (
                    <div className="password-requirements">
                      {requirements.map((req, i) => (
                        <div key={i} className={`requirement ${req.met ? 'met' : ''}`}>
                          {req.met ? <Check size={12} /> : <span className="requirement-dot"></span>}
                          <span>{req.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="login-field">
                  <label>Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    className={confirmPassword && password !== confirmPassword ? 'input-error' : ''}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <span className="field-error">Passwords don't match</span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !allRequirementsMet || password !== confirmPassword}
                  className="login-submit"
                >
                  {loading ? (
                    'Sending code...'
                  ) : (
                    <>
                      <Mail size={20} />
                      Send Verification Code
                    </>
                  )}
                </button>
              </form>

              <div className="login-footer">
                <span>Already have an account?</span>
                <Link to="/login" className="login-link">
                  Sign in <ArrowRight size={16} />
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="login-form-header">
                <h2>Verify Email</h2>
                <p>Enter the code sent to <strong>{email}</strong></p>
              </div>

              <form onSubmit={handleVerifyOTP} className="login-form">
                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="login-field">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    required
                    autoFocus
                    maxLength={6}
                    className="otp-input"
                  />
                  <span className="field-hint">Check your email for the verification code</span>
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="login-submit"
                >
                  {loading ? (
                    'Verifying...'
                  ) : (
                    <>
                      <Check size={20} />
                      Verify & Create Account
                    </>
                  )}
                </button>

                <div className="otp-actions">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || loading}
                    className="resend-btn"
                  >
                    <RefreshCw size={16} />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(''); setError(''); }}
                    className="back-to-form-btn"
                  >
                    Change Email
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
